import * as CustomerServiceActions from "./customer-service.actions";

import {createReducer, on} from "@ngrx/store";
import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {TenCustomerService, TenCustomerServiceSearchParams, TenHistory} from "../../_models/customer-service.models";
import {DahLoadingType} from '@common/components-module/core-components.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {CategoryHash, OrderCategory} from '@applications/_common/models/order.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const customerServiceFeatureKey = 'customerService';

export function sortByDate(a: TenCustomerService, b: TenCustomerService) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'created');
}

export const listAdapter: EntityAdapter<TenCustomerService> = createEntityAdapter<TenCustomerService>({
    sortComparer: sortByDate
});

export function sortCommentsByDate(a: TenHistory, b: TenHistory) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'created');
}

export const commentsAdapter: EntityAdapter<TenHistory> = createEntityAdapter<TenHistory>({
    sortComparer: sortCommentsByDate
});

export interface CustomerServiceState {
    list: EntityStateModel<TenCustomerService, TenCustomerServiceSearchParams>,
    categoryHashes: CategoryHash[],
    categories: OrderCategory[],
    openedItem: {
        main?: TenCustomerService,
        comments: EntityStateModel<TenHistory, any>
    }
}

export const initialState: CustomerServiceState = {
    list: listAdapter.getInitialState({
        listLoading: 'listLoading',
        pagination: {},
        itemsLoading: []
    }),
    categoryHashes: [],
    categories: [],
    openedItem: {
        comments: commentsAdapter.getInitialState({
            listLoading: 'listLoading',
            pagination: {size: 30},
            itemsLoading: []
        })
    }
};

export const customerServiceReducer = createReducer(
    initialState,

    // <editor-fold desc="Customer Service List">
    on(CustomerServiceActions.LoadList, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.list.ids)
        }
    })),

    on(CustomerServiceActions.LoadListSuccess, (state, action) => ({
        ...state,
        list: action.payload.clearState
            ? listAdapter.setAll(action.payload.items, {
                ...state.list,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : listAdapter.upsertMany(action.payload.items, {
                ...state.list,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
    })),

    on(CustomerServiceActions.LoadListFailure, (state) => ({
        ...state,
        list: {
            ...state.list,
            listLoading: undefined
        }
    })),

    on(CustomerServiceActions.SetPagination, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            pagination: {
                ...state.list.pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state.list.pagination, state.list.ids.length) : 0
            }
        }
    })),

    on(CustomerServiceActions.SetFilter, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            filter: action.payload.filter
        }
    })),

    on(CustomerServiceActions.SetCategoryHashesSuccess, (state, action) => ({
        ...state,
        categoryHashes: action.payload.categoryHashes
    })),

    on(CustomerServiceActions.SetCategoriesSuccess, (state, action) => ({
        ...state,
        categories: action.payload.categories
    })),

    on(CustomerServiceActions.AddItem, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, true)
        }
    })),

    on(CustomerServiceActions.AddItemSuccess, (state, action) => ({
        ...state,
        ...isAvailableByFilter(state.list.filter, action.payload.item, state.categoryHashes) && {
            list: listAdapter.addOne(action.payload.item, {
                ...state.list,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
            })
        }
    })),

    on(CustomerServiceActions.AddItemFailure, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
        }
    })),

    on(CustomerServiceActions.EditItem, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, true)
        }
    })),

    on(CustomerServiceActions.EditItemSuccess, (state, action) => ({
        ...state,
        list: isAvailableByFilter(state.list.filter, action.payload.item, state.categoryHashes)
            ? listAdapter.setOne(action.payload.item, {
                ...state.list,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
            })
            : listAdapter.removeOne(action.payload.item.id, {
                ...state.list,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
            }),
        openedItem: {
            ...state.openedItem,
            ...state.openedItem.main?.id && {
                main: {
                    ...action.payload.item
                }
            }
        }
    })),

    on(CustomerServiceActions.EditItemFailure, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
        }
    })),

    on(CustomerServiceActions.DeleteItem, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, true)
        }
    })),

    on(CustomerServiceActions.DeleteItemSuccess, (state, action) => ({
        ...state,
        list: listAdapter.removeOne(action.payload.item.id, {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
        }),
        openedItem: {...initialState.openedItem}
    })),

    on(CustomerServiceActions.DeleteItemFailure, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.item.id, false)
        }
    })),

    on(CustomerServiceActions.SubscribeOnItemSuccess, (state, action) => ({
        ...state,
        list: listAdapter.updateOne(
            {
                id: action.payload.item.id,
                changes: {subscribed: action.payload.subscribed, subscribersCount: action.payload.subscribersCount}
            }, {...state.list}
        )
    })),
    // </editor-fold desc="Customer Service List">

    // <editor-fold desc="Customer Service Item">
    on(CustomerServiceActions.SetOpenedItem, (state, action) => ({
        ...state,
        openedItem: {
            main: action.payload.item,
            comments: {...initialState.openedItem.comments}
        }
    })),

    on(CustomerServiceActions.UpdateOpenedItem, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            main: action.payload.item
        }
    })),
    // <editor-fold>

    // <editor-fold desc="Customer Service Comments">
    on(CustomerServiceActions.LoadComments, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                pagination: {
                    ...state.openedItem.comments.pagination,
                    number: action.payload.next ? StoredComponentHelperService.setNextPage(state.openedItem.comments.pagination, state.openedItem.comments.ids.length) : 0
                },
                listLoading: action.payload.next ? 'listLoadNext' as DahLoadingType : 'listLoading' as DahLoadingType
            }
        }
    })),

    on(CustomerServiceActions.LoadCommentsSuccess, (state, action) => {
        return {
            ...state,
            openedItem: {
                ...state.openedItem,
                comments: action.payload?.next
                    ? commentsAdapter.upsertMany(action.payload.comments, {
                        ...state.openedItem.comments,
                        pagination: action.payload.pagination,
                        listLoading: undefined
                    })
                    : commentsAdapter.setAll(action.payload.comments, {
                        ...state.openedItem.comments,
                        pagination: action.payload.pagination,
                        listLoading: undefined
                    })
            }
        };
    }),

    on(CustomerServiceActions.LoadCommentsFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined
            }
        }
    })),

    on(CustomerServiceActions.SendComment, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(CustomerServiceActions.EditComment, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, true),
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(CustomerServiceActions.SendCommentSuccess, (state, action) => ({
        ...state,

        list: listAdapter.setOne({
            ...state.openedItem.main!,
            historyCount: state.openedItem.main!.historyCount + 1
        }, state.list),

        openedItem: {
            main: {
                ...state.openedItem.main!,
                historyCount: state.openedItem.main!.historyCount + 1
            },
            comments: commentsAdapter.addOne(action.payload.item, {
                ...state.openedItem.comments,
                listLoading: undefined
            })
        }
    })),

    on(CustomerServiceActions.EditCommentSuccess, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: commentsAdapter.setOne(action.payload.item, {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.item.id, false),
                listLoading: undefined
            })
        }
    })),

    on(CustomerServiceActions.DeleteComment, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, true),
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(CustomerServiceActions.DeleteCommentSuccess, (state, action) => ({
        ...state,

        list: listAdapter.updateOne({
            id: state.openedItem.main!.id,
            changes: {
                historyCount: StoredComponentHelperService.setTotalElements(action.payload.itemId, state.openedItem.comments.pagination, state.openedItem.comments.ids, true)
            }
        }, state.list),

        openedItem: {
            ...state.openedItem,
            comments: commentsAdapter.removeOne(action.payload.itemId, {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, false),
                listLoading: undefined,

                pagination: {
                    ...state.openedItem.comments.pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.itemId, state.openedItem.comments.pagination, state.openedItem.comments.ids, true)
                }
            })
        }
    })),

    on(CustomerServiceActions.SendCommentFailure, CustomerServiceActions.EditCommentFailure, CustomerServiceActions.DeleteCommentFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined
            }
        }
    })),
    // <editor-fold>

    on(CustomerServiceActions.SetInitialState, () => ({...initialState})),

    on(SetAllInitialStates, () => ({...initialState})),
);


const isAvailableByFilter = (searchParams: TenCustomerServiceSearchParams | undefined, item: TenCustomerService, categoryHashes: CategoryHash[]): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.query && item.content && item.content.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) {
        return false;
    }

    if (searchParams.statuses && searchParams.statuses.indexOf(item.status) == -1) {
        return false;
    }

    if (searchParams.categoryHashes) {
        const categoryNames = searchParams.categoryHashes.map(categoryId => {
            return categoryHashes.find((el) => el.hash === categoryId)!.name;
        });

        if (categoryNames.indexOf(item.categoryName) == -1) {
            return false;
        }
    }

    return true;
};
